// Catalog.ID Client — Lazy pdfmake Loader & Letterhead Output
// ============================================================================
// Dynamic-imports pdfmake + its bundled fonts so the ~2 MB library lands in a
// separate Vite chunk, fetched only when a document is actually generated.
// Mirrors portal/src/modules/base/pdfMakeLoader.ts.
//
// downloadPdf/printPdf are the output paths for all generated documents:
// they give the doc-definition the Catalog letterhead as its page background
// (withLetterhead) before rendering, so every document carries it.

import type { TDocumentDefinitions } from 'pdfmake/interfaces';
import { withLetterhead } from './letterhead';

export interface PdfDocument {
    download(filename: string): void;
    print(): void;
    open(): void;
    getBuffer(cb: (buffer: Uint8Array) => void, options?: { autoPrint?: boolean }): void;
}

// pdfmake's browser build has no useful module-level types; the surface we
// use is re-typed as PdfDocument above.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let pdfMakeInstance: any = null;

async function loadPdfMake(): Promise<typeof pdfMakeInstance> {
    if (pdfMakeInstance) return pdfMakeInstance;

    const pdfMakeModule = await import('pdfmake/build/pdfmake');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pdfMake: any = (pdfMakeModule as any).default ?? pdfMakeModule;

    const vfsModule = await import('pdfmake/build/vfs_fonts');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const vfs: any = (vfsModule as any).default ?? vfsModule;
    // vfs_fonts export shape varies across pdfmake versions
    pdfMake.vfs = vfs.vfs ?? vfs.pdfMake?.vfs ?? vfs;

    pdfMakeInstance = pdfMake;
    return pdfMakeInstance;
}

/** Roboto Regular out of pdfmake's OWN vfs — the exact face it draws the page
 *  footer with, so anything stamped on top of its output with another engine
 *  (booklet/kit-merge.ts) lines up by construction. */
export async function loadRobotoRegular(): Promise<Uint8Array> {
    const pdfMake = await loadPdfMake();
    const base64: unknown = pdfMake.vfs?.['Roboto-Regular.ttf'];
    if (typeof base64 !== 'string') throw new Error('pdfmake\'s font store has no Roboto-Regular.ttf');
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
}

/** Create a PDF from a doc-definition. Loads pdfmake on first use. */
export async function createPdf(dd: TDocumentDefinitions): Promise<PdfDocument> {
    const pdfMake = await loadPdfMake();
    return pdfMake.createPdf(dd) as PdfDocument;
}

function pdfBuffer(pdf: PdfDocument, options?: { autoPrint?: boolean }): Promise<Uint8Array<ArrayBuffer>> {
    // pdfmake hands back a plain ArrayBuffer-backed array (never a
    // SharedArrayBuffer), which is what Blob wants; the cast just says so.
    return new Promise(resolve => pdf.getBuffer(b => resolve(b as Uint8Array<ArrayBuffer>), options));
}

/** Generate the document with the letterhead and download it. */
export async function downloadPdf(dd: TDocumentDefinitions, filename: string): Promise<void> {
    const pdf = await createPdf(withLetterhead(dd));
    const bytes = await pdfBuffer(pdf);
    downloadBlob(new Blob([bytes], { type: 'application/pdf' }), filename);
}

export interface PrintPdfOptions {
    /** Last chance to rework the rendered bytes before they reach the print
     *  window — the kit appends and stamps the Membership Agreement here.
     *  Nothing is written to disk either way. */
    transform?: (bytes: Uint8Array) => Promise<Uint8Array>;
}

/** The browser refused the print window — pop-ups are blocked for this site,
 *  or the window was not opened inside a click (see {@link printPdf}). */
export class PrintWindowBlockedError extends Error {
    constructor() {
        super('The browser blocked the print window');
        this.name = 'PrintWindowBlockedError';
    }
}

/**
 * Generate the document with the letterhead and open it in a new window
 * with an auto-print action. Mirrors pdfmake's own print(): the window is
 * opened before any async work so popup blockers attribute it to the user
 * gesture, and autoPrint embeds a PDF OpenAction that the viewer runs.
 * Like pdfmake, the object URL is left for
 * page-unload cleanup — the window needs it for as long as it stays open.
 *
 * That only holds when the CALLER is still inside the click: anything it awaits
 * first (a server call, key derivation) can outlast the browser's few seconds
 * of user activation, and the window is blocked. Start a print from a click of
 * its own — `components/print-button.ts` does exactly that.
 */
export async function printPdf(dd: TDocumentDefinitions, options: PrintPdfOptions = {}): Promise<void> {
    const win = window.open('', '_blank');
    if (win === null) throw new PrintWindowBlockedError();
    try {
        const pdf = await createPdf(withLetterhead(dd));
        const rendered = await pdfBuffer(pdf, { autoPrint: true });
        const bytes = options.transform ? await options.transform(rendered) : rendered;
        win.location.href = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
    } catch (err) {
        win.close();
        throw err;
    }
}

export function downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}
